import {v4 as uuidv4} from "uuid";
import {none, option} from "ts-option";
import BaseFormController from "../../../validation/BaseFormController";
import {FormField, RequiredFormField} from "../../../validation/FormField";
import FormValidators from "../../../validation/FormValidators";
import FormValidation from "../../../validation/FormValidation";
import JobStatus from "../../../model/job/JobStatus";
import {JobStatusExternalCategoryEnum} from "../../../model/job/JobStatusExternalCategory";

export default class JobStatusForm extends BaseFormController<JobStatusForm> {

	constructor(public readonly formId: string,
		public readonly label: RequiredFormField<string>,
		public readonly category: RequiredFormField<string>,
		public readonly externalLabel: RequiredFormField<string>,
		public readonly externalCategory: RequiredFormField<JobStatusExternalCategoryEnum>,
		public readonly description: RequiredFormField<string>,
		public readonly showPushNotification: RequiredFormField<boolean>,
		public readonly fieldWithFocus: FormField<string>) {
		super(fieldWithFocus, formId);
	}

	public static createEmpty(): JobStatusForm {
		return this.createJobStatusForm({showPushNotification: option(false)});
	}

	public static createFromJobStatus(jobStatus: JobStatus): JobStatusForm {
		return this.createJobStatusForm({
			label: option(jobStatus.label),
			category: option(jobStatus.category),
			externalLabel: option(jobStatus.externalLabel),
			externalCategory: option(jobStatus.externalCategory),
			description: option(jobStatus.description),
			showPushNotification: option(!!jobStatus.showPushNotification)
		});
	}

	private static createJobStatusForm({
		label = none,
		category = none,
		externalLabel = none,
		externalCategory = none,
		description = none,
		showPushNotification = option(false)
	}) {
		const labelField = RequiredFormField.create<string>(label, "Label is required", [
			FormValidators.nonEmptyString("Label is required")
		]);
		return new JobStatusForm(
			uuidv4(),
			labelField,
			RequiredFormField.create<string>(category, "Category is required", [
				FormValidators.nonEmptyString("Category is required")
			]),
			RequiredFormField.create<string>(externalLabel, "External label is required", [
				FormValidators.nonEmptyString("External label is required"),
				FormValidators.maxLength("Max length is 40 characters", 40),
			]),
			RequiredFormField.create<JobStatusExternalCategoryEnum>(externalCategory, "External category is required", [
				FormValidators.nonEmptyString("External category is required")
			]),
			RequiredFormField.create<string>(description, "Description is required", [
				FormValidators.nonEmptyString("Description is required")
			]),
			RequiredFormField.create<boolean>(showPushNotification, "Show push notification is required"),
			labelField
		);
	}

	onValid(callback: (data: JobStatus) => void) {
		const result = FormValidation.validateFields(
			[this.label, this.category, this.externalLabel, this.externalCategory, this.description, this.showPushNotification],
			() => {
				return new JobStatus(
					uuidv4(),
					this.label.getValue(),
					this.category.getValue(),
					this.externalLabel.getValue(),
					this.externalCategory.getValue(),
					this.description.getValue(),
					false,
					this.showPushNotification.getValue(),
				)
			}
		);
		result.onSuccess((data) => {
			callback(data);
		});
	}

	copy({
		label = this.label,
		category = this.category,
		externalLabel = this.externalLabel,
		externalCategory = this.externalCategory,
		description = this.description,
		showPushNotification = this.showPushNotification
	}): JobStatusForm {
		return new JobStatusForm(this.formId,
			label,
			category,
			externalLabel,
			externalCategory,
			description,
			showPushNotification,
			this.fieldWithFocus);
	}

	fields(): Array<FormField<any>> {
		return [this.label, this.category, this.externalLabel, this.externalCategory, this.description,
			this.showPushNotification];
	}
}
